import {useEffect, useRef} from "react";
import axios from "axios";
import {useSearchParams} from "react-router-dom";
import useAppContext from "../../util/AppContext";
import {SessionContext} from "../../util/SessionContext";
import EventBus from "../../util/EventBus";

export default function TokenExchange() {
    const sessionContext: SessionContext = useAppContext()?.sessionContext!;
    const [searchParams, setSearchParams] = useSearchParams();
    const exchangedRef = useRef(false);

    useEffect(() => {
        const t1AccessToken = searchParams.get("t1AccessToken");
        if (!t1AccessToken || exchangedRef.current) return;
        exchangedRef.current = true;

        axios({
            method: "POST",
            baseURL: process.env.REACT_APP_API_URL,
            // DEAD ON EVERY TIER, and left pointing at t5 so that stays visible. No repo in the
            // suite -- t1, t2, t5, t6, t7 -- defines an exchange-token route or handler, so this
            // call has never reached anything; the Data Catalog's own frontend makes it too.
            // Repointing it at t10 would only move a broken call to a newer address.
            url: "t5/v1/exchange-token",
            headers: {
                "X-Cres-AccessToken": t1AccessToken,
            },
        }).then(response => {
            let visibilityMode, visibilityValue;
            const account = response.data["Account"];
            if (account?.Positions?.length > 0) {
                visibilityMode = "Position";
                visibilityValue = account.Positions[0].USID;
            } else if (account?.UserGroups?.length > 0) {
                visibilityMode = "UserGroup";
                visibilityValue = account.UserGroups[0].USID;
            } else if (account?.WorkGroups?.length > 0) {
                visibilityMode = "WorkGroup";
                visibilityValue = account.WorkGroups[0].USID;
            }

            sessionContext.setIdentityToken(response.data["IdentityToken"]);
            sessionContext.setSessionUSID(response.data["Session"]);
            sessionContext.setAccessToken(response.data["AccessToken"]);
            sessionContext.setRefreshToken(response.data["RefreshToken"]);
            if (visibilityMode) sessionContext.setActiveVisibility(visibilityMode, visibilityValue!);
            if (account) {
                sessionContext.setAccountName(account["AccountName"] ?? account["AccountName"]);
                sessionContext.setAccountUserUSID(account["AccountUserUSID"] ?? account["AccountUserUSID"]);
                sessionContext.setAccount(account);
            }
            sessionContext.setRoles(response.data["Roles"]);
            sessionContext.setPrivileges(response.data["Privileges"]);
            sessionContext.setProducts(response.data["Products"] || []);
            sessionContext.setPrivilegeConstraints(response.data["PrivilegeConstraints"]);
            sessionContext.setTenants(response.data["Tenants"]);
            sessionContext.setPickValues(response.data["PickValues"]);
            sessionContext.setShowLoginDialog(false);

            const next = new URLSearchParams(searchParams);
            next.delete("t1AccessToken");
            setSearchParams(next, {replace: true});
        }).catch(() => {
            EventBus.error.emit("show-system-error");
            const next = new URLSearchParams(searchParams);
            next.delete("t1AccessToken");
            setSearchParams(next, {replace: true});
        });
    }, [searchParams, sessionContext, setSearchParams]);

    return null;
}
